"use client";

import { useQuery } from "@tanstack/react-query";
import type { PlatformMeta } from "@theta/api-client";
import { thetaApi } from "../api/theta";

export const platformMetaKey = ["v1", "meta"] as const;

/**
 * `GET /meta` (§2.1) — what the server will accept, asked once per session.
 *
 * The endpoint's own header says why a client reads it rather than compiling
 * the numbers in: they are "policy constants that would otherwise be
 * duplicated as magic numbers in two consumers and drift". The upload limits
 * are the first of them the web app actually needs — `useAttachments` refuses
 * an oversized file before spending three minutes uploading it — and an
 * operator can move any of them by env, so a constant in the bundle would be
 * wrong the moment they did.
 *
 * IT NEVER GOES STALE ON PURPOSE. The answer touches no database and changes
 * only when the service restarts, so refetching it on every mount or window
 * focus buys nothing; a reload picks up a redeploy. A failure leaves `data`
 * undefined, and every caller must treat that as "don't know" rather than
 * "no limits" — the server is still the one that decides.
 */
export function usePlatformMeta() {
  return useQuery<PlatformMeta>({
    queryKey: platformMetaKey,
    queryFn: () => thetaApi.meta(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Just the upload limits, `undefined` until they are known. */
export function useUploadLimits(): PlatformMeta["uploads"] | undefined {
  return usePlatformMeta().data?.uploads;
}
